import { useCallback, useEffect, useRef, useState } from 'react';
import type { DateRange } from '@components/DateRangeSelector';
import { fromPickerDate } from '@shared/dates/calendarDate';
import { endOfDay, startOfDay, subDays } from 'date-fns';

const STORAGE_PREFIX = 'jotbill-filters-';

function getDefault30Days(): DateRange {
  const today = new Date();
  return {
    start: startOfDay(subDays(today, 29)),
    end: endOfDay(today),
  };
}

function toISODate(date: Date | null): string | undefined {
  if (!date) return undefined;
  return fromPickerDate(date) ?? undefined;
}

interface StoredFilters {
  segment?: string;
  filterValues?: Record<string, string>;
  filtersOpen?: boolean;
  page?: number;
  perPage?: number;
  /** Full ISO timestamps for restoring DateRange with correct local time */
  dateStartISO?: string | null;
  dateEndISO?: string | null;
  /** Only true after the user has explicitly changed the date filter */
  dateSet?: boolean;
}

function loadFromSession(tabKey: string): StoredFilters | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + tabKey);
    if (!raw) return null;
    return JSON.parse(raw) as StoredFilters;
  } catch {
    return null;
  }
}

function saveToSession(tabKey: string, filters: StoredFilters): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + tabKey, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

interface UseTabFiltersOptions {
  tabKey: string;
  defaultSegment?: string;
  defaultPerPage?: number;
}

export function useTabFilters({
  tabKey,
  defaultSegment = 'all',
  defaultPerPage = 25,
}: UseTabFiltersOptions) {
  // Load persisted state once on mount
  const stored = useRef(loadFromSession(tabKey)).current;

  const [activeSegment, setActiveSegmentRaw] = useState(stored?.segment ?? defaultSegment);
  const [filterValues, setFilterValuesRaw] = useState<Record<string, string>>(stored?.filterValues ?? {});
  const [filtersOpen, setFiltersOpenRaw] = useState(stored?.filtersOpen ?? false);
  const [page, setPageRaw] = useState(stored?.page ?? 1);
  const [perPage, setPerPageRaw] = useState(stored?.perPage ?? defaultPerPage);

  // dateSet is state — only flipped to true when the user explicitly changes the date.
  // This prevents the first-render useEffect from poisoning future sessions with
  // stale absolute timestamps from the default 30-day window.
  const [dateSet, setDateSet] = useState(stored?.dateSet ?? false);

  const [dateRange, setDateRangeRaw] = useState<DateRange>(() => {
    if (stored?.dateSet) {
      return {
        start: stored.dateStartISO ? new Date(stored.dateStartISO) : null,
        end: stored.dateEndISO ? new Date(stored.dateEndISO) : null,
      };
    }
    return getDefault30Days();
  });

  const dateFrom = toISODate(dateRange.start);
  const dateTo = toISODate(dateRange.end);

  // Persist ALL state to sessionStorage on every change via useEffect
  useEffect(() => {
    saveToSession(tabKey, {
      segment: activeSegment,
      filterValues,
      filtersOpen,
      page,
      perPage,
      dateStartISO: dateRange.start?.toISOString() ?? null,
      dateEndISO: dateRange.end?.toISOString() ?? null,
      dateSet,
    });
  }, [tabKey, activeSegment, filterValues, filtersOpen, page, perPage, dateRange, dateSet]);

  // Wrapped setters that handle page reset

  const setActiveSegment = useCallback((segment: string) => {
    setActiveSegmentRaw(segment);
    setPageRaw(1);
  }, []);

  const setFilterValue = useCallback((key: string, value: string) => {
    setFilterValuesRaw(prev => ({ ...prev, [key]: value }));
    setPageRaw(1);
  }, []);

  const toggleFilters = useCallback(() => {
    setFiltersOpenRaw(prev => !prev);
  }, []);

  const setPage = useCallback((p: number) => {
    setPageRaw(p);
  }, []);

  const setPerPage = useCallback((pp: number) => {
    setPerPageRaw(pp);
    setPageRaw(1);
  }, []);

  const onDateRangeChange = useCallback((range: DateRange) => {
    setDateRangeRaw(range);
    setDateSet(true); // only set on explicit user interaction
    setPageRaw(1);
  }, []);

  return {
    activeSegment,
    setActiveSegment,
    filterValues,
    setFilterValue,
    filtersOpen,
    toggleFilters,
    page,
    setPage,
    perPage,
    setPerPage,
    dateRange,
    dateFrom,
    dateTo,
    onDateRangeChange,
  };
}
